import React, { useCallback } from "react"
import { Layout, Table, Typography, Tag, Space, Button, Row, Col, Divider, Empty } from "antd"
import { StarOutlined, EyeOutlined, CloseOutlined } from "@ant-design/icons"
import moment from "moment"

const { Text, Title } = Typography
const { Content } = Layout

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: '24px'
  },
  header: {
    marginBottom: 24,
    borderBottom: "1px solid #f0f0f0",
    paddingBottom: 16,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  headerStats: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  matchTag: {
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "12px",
  },
  candidateHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    textAlign: "center",
  },
  actionIcons: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    marginTop: "8px",
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto'
  }
}

const CompareView = ({ 
  candidates = [], 
  onClose, 
  onViewCandidate,
  onRemoveFromCompare
}) => {
  const handleRemove = useCallback((candidateId) => {
    onRemoveFromCompare?.(candidateId);
    if (candidates.length <= 2) {
      onClose?.();
    }
  }, [candidates.length, onClose, onRemoveFromCompare]);

  const comparisonFields = [
    {
      section: 'Basic Information',
      fields: [
        { key: 'name', label: 'Name' },
        { key: 'location', label: 'Location' },
        { key: 'workAuth', label: 'Work Authorization' },
        {
          key: 'dateModified',
          label: 'Last Updated',
          render: (date) => date ? moment(date).format('LL') : '-'
        }
      ]
    },
    {
      section: 'Current Position',
      fields: [
        {
          key: 'currentPosition',
          label: 'Current Role',
          render: (pos) => pos ? `${pos.title || '-'} at ${pos.company || '-'}` : '-'
        }
      ]
    },
    {
      section: 'Previous Position',
      fields: [
        {
          key: 'previousPosition',
          label: 'Previous Role',
          render: (pos) => pos ? `${pos.title || '-'} at ${pos.company || '-'}` : '-'
        }
      ]
    },
    {
      section: 'Education & Skills',
      fields: [
        { key: 'education', label: 'Education Level' },
        {
          key: 'metadata',
          label: 'Additional Information',
          render: (metadata) => {
            if (!metadata) return '-';
            const info = [];
            if (metadata.smsOptIn) info.push('SMS Opt-in');
            if (metadata.isVeteran) info.push('Veteran');
            if (metadata.isDiversity) info.push('Diversity');
            return info.length > 0 ? info.join(', ') : '-';
          }
        }
      ]
    },
    {
      section: 'Target Information',
      fields: [
        { key: 'targetJobTitle', label: 'Target Role' }
      ]
    }
  ];

  // Transform data for Table component
  const dataSource = comparisonFields.flatMap(section => [
    { key: section.section, isSection: true, name: section.section },
    ...section.fields.map(field => ({
      key: field.key,
      name: field.label,
      ...candidates.reduce((acc, candidate, idx) => {
        const value = field.render
          ? field.render(candidate[field.key])
          : candidate[field.key] || '-';
        acc[`candidate${idx}`] = value;
        return acc;
      }, {})
    }))
  ]);

  const columns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
      width: 200,
      fixed: "left",
      render: (text, record) => record.isSection ? <Title level={5}>{text}</Title> : <Text strong>{text}</Text>,
    },
    ...candidates.map((candidate, index) => ({
      title: () => (
        <div style={styles.candidateHeader}>
          <Tag color="success" style={styles.matchTag}>
            {candidate.relevance}% MATCH
          </Tag>
          <Title level={5} style={{ margin: '8px 0' }}>{candidate.name}</Title>
          <Text type="secondary">{candidate.location}</Text>
          <div style={styles.actionIcons}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onViewCandidate?.(candidate);
              }}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(candidate.id);
              }}
            />
          </div>
        </div>
      ),
      dataIndex: `candidate${index}`,
      key: `candidate${index}`,
      width: 250,
    })),
  ];

  if (!candidates.length) {
    return (
      <Layout style={styles.container}>
        <Empty description="No candidates selected for comparison" />
      </Layout>
    );
  }

  return (
    <Layout style={styles.container}>
      <div style={styles.header}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Comparing {candidates.length} Candidates
            </Title>
          </Col>
          <Col>
            <Button type="primary" onClick={onClose}>
              Back to Results
            </Button>
          </Col>
        </Row>
      </div>
      <div style={styles.tableContainer}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
          bordered
          size="middle"
          sticky
        />
      </div>
    </Layout>
  );
};

export default CompareView;

